import { inject, Injectable } from '@angular/core';
import { ForecastVersionResponse } from '@api-response-types/forecast-version.response';
import { ForecastProgressSseEventPayload } from '@api-types/sse/event-payloads/forecast-progress.sse-event-payload';
import { SseEventType } from '@api-types/sse/sse.types';
import { ForecastProgressSignalService } from '@core/sse/forecast-progress-signal.service';
import { SseEventHandler } from '@core/sse/sse-event-handler';
import { Store } from '@ngrx/store';
import { ForecastVersionActions } from './forecast-version.actions';
import { selectAllForecastVersions } from './forecast-version.selectors';

// updates forecast version in store or refetches if version is unknown (medicos scenario)
@Injectable()
export class ForecastProgressSseHandler implements SseEventHandler<
  typeof SseEventType.ForecastProgress
> {
  readonly eventType = SseEventType.ForecastProgress;

  private readonly store = inject(Store);
  private readonly forecastProgressSignal = inject(
    ForecastProgressSignalService,
  );
  private readonly forecastVersions = this.store.selectSignal(
    selectAllForecastVersions,
  );

  handle(payload: ForecastProgressSseEventPayload): void {
    const version = payload.forecastVersion;

    // always update signal service so home page cards get live updates
    this.forecastProgressSignal.update(version);

    // NgRx demand forecasting state is lazy-loaded, so it may not exist yet (e.g. on the home page)
    let forecastVersions: ForecastVersionResponse[] | undefined;
    try {
      forecastVersions = this.forecastVersions();
    } catch {
      // state not loaded yet, signal service update above is sufficient
      return;
    }

    const existsInStore = forecastVersions.some((v) => v.id === version.id);

    if (!existsInStore) {
      // Version is not yet in the local store. Happens whenever a progress
      // event arrives for a version the frontend hasn't loaded — e.g. a
      // calculation running on a different configuration than the one the
      // user is viewing, or a version created outside the UI (medicos).
      //
      // Previously this dispatched getAllForecastVersionsForCofiguration,
      // but the effect behind that action ignores the action's configurationId
      // and refetches the URL's configuration instead, then writes the
      // result through setAllForecastVersions which replaces the entire
      // versions array. That hands out brand-new object references for every
      // version, invalidating rxResource/httpResource dependencies keyed on
      // selectedVersion()/selectForecastVersionByQueryParam and triggering
      // cascading reloads (quick filters, measures, prediction explanation)
      // on unrelated version detail pages.
      //
      // The SSE payload already carries the full ForecastVersionResponse, so
      // we can add it directly without any HTTP round-trip. addForecastVersion
      // appends by id while preserving existing refs for other versions.
      this.store.dispatch(
        ForecastVersionActions.addForecastVersion({ forecastVersion: version }),
      );
      return;
    }

    this.store.dispatch(
      ForecastVersionActions.setForecastVersion({ forecastVersion: version }),
    );
  }
}
